/**
 * @citeclerk/goodlaw/authorityKind.js — RUN JE: WHICH QUESTION MAY BE ASKED OF WHICH AUTHORITY.
 *
 * ── THE DEFECT CLASS ────────────────────────────────────────────────────────────────────────────
 * "Good law" is a TREATMENT concept: has a court overruled, abrogated, distinguished or criticised
 * this DECISION. A statute is not treated; it is amended or repealed, which is a CURRENCY question
 * the code itself answers (govinfo/GPO, eCFR) and no citator can. A constitutional holding cannot be
 * "superseded by statute" — a statute is subordinate to the provision the holding construes.
 *
 * Both directions of that error shipped:
 *   · Draft's ratify-all modal (2026-08-23, matter 3211) rendered its ten eligible STATUTES directly
 *     above "16 · good-law status not yet verified" — a treatment vocabulary applied, on screen, to
 *     a bundle that was half statutes. The Wall card asked "Good law: Unknown · As-of date
 *     unavailable" of 29 U.S.C. § 216, whose as-of date the row already carried in its statute pin.
 *   · RUN DC/EB found *Heller* and *Bruen* stamped `superseded_by_statute` and filtered out of the
 *     ratified bundle; the rows were corrected, the rule that produced them was not.
 *
 * ── THE RULE, AS A POSITIVE ALLOW-LIST ──────────────────────────────────────────────────────────
 * A treatment question is asked ONLY of a kind in TREATABLE_FAMILIES. Everything else is not
 * "unverified" — the question does not apply, and its absence must never block ratification,
 * filter a bundle, or render as a not-yet-verified state. It is an allow-list, not a deny-list,
 * because a deny-list fails OPEN on every shape it does not enumerate (RUN GS2's six false-denial
 * paths; RUN GS1's `verifyCapabilities` keyed by type so a whole family inherits a column that is
 * wrong for its members). The kinds are enumerated from the product's own registry — the 7
 * families / 26 sub-types of apps/main's Manual Cite `families.js` — and `authorityKind.test.js`
 * reads that file and fails if the two ever drift.
 *
 * PURE / ISOMORPHIC, like the rest of this package: no I/O, no DB, no app state.
 */

// The registry, mirrored. Keys are the literal `key:` values of apps/main/client/src/screens/
// ManualCite/families.js — family → sub-types. The test asserts this against the source text.
export const REGISTRY = Object.freeze({
  case: Object.freeze(['case', 'database', 'slipop', 'online', 'neutral']),
  statute: Object.freeze(['code', 'session', 'rule', 'restatement', 'ucc']),
  constitution: Object.freeze(['constitution']),
  regulation: Object.freeze(['cfr', 'revrul', 'treasreg']),
  legislative: Object.freeze(['bill', 'report', 'statebill', 'hearing', 'floor']),
  webpage: Object.freeze(['webpage']),
  secondary: Object.freeze(['book', 'journal', 'magazine', 'newspaper', 'chapter', 'courtdoc']),
});
export const REGISTRY_FAMILIES = Object.freeze(Object.keys(REGISTRY));
export const REGISTRY_SUBTYPES = Object.freeze(Object.values(REGISTRY).flat());

// Non-registry kinds the apps store as `source_type` and that are never authorities at all.
export const RECORD_KIND = 'record';

/** ⭐ THE ALLOW-LIST. The only family a treatment verdict may be requested of. */
export const TREATABLE_FAMILIES = Object.freeze(['case']);

/**
 * What the honest health line for a kind IS:
 *   treatment — a citator answer ("no negative treatment detected as of <date>", never "good law").
 *   currency  — an as-of date from the authoritative code (govinfo/GPO, eCFR); absent → "not checked".
 *   none      — neither question applies; the card says so rather than reporting "pending".
 */
export const REGISTER = Object.freeze({ TREATMENT: 'treatment', CURRENCY: 'currency', NONE: 'none' });
const FAMILY_REGISTER = Object.freeze({
  case: REGISTER.TREATMENT,
  statute: REGISTER.CURRENCY,
  regulation: REGISTER.CURRENCY,
  constitution: REGISTER.NONE,
  legislative: REGISTER.NONE,
  webpage: REGISTER.NONE,
  secondary: REGISTER.NONE,
});
// Sub-type overrides: a Restatement is re-issued, not amended — no code carries its currency.
const SUBTYPE_REGISTER = Object.freeze({ restatement: REGISTER.NONE });

// Draft's `source_type` vocabulary → registry family. Anything not listed is UNKNOWN → not treatable.
const SOURCE_TYPE_FAMILY = Object.freeze({
  CASE: 'case',
  STATUTE: 'statute', // refined to `regulation` below when the citation is a C.F.R. section
  BRIEF: 'secondary', // a court document (families.js: secondary/courtdoc)
});
const RECORD_TYPES = new Set(['record', 'evidence', 'fact_document', 'pleading', 'transcript', 'exhibit', 'declaration']);
const CFR_RE = /^\s*\d+\s*C\.?\s*F\.?\s*R\.?\s*[§\s]/i;

/**
 * normalizeAuthorityKind(input) → { family, subtype } | { family:'record', subtype:null } | null
 *
 * Accepts a registry family key, a registry sub-type key, a Draft source_type ('CASE' / 'STATUTE' /
 * 'pleading' …), or a source-shaped object ({ source_type | type | kind | family | subtype,
 * full_citation | citation }). Returns null for anything it does not recognise — and null is NOT
 * treatable (fail closed; an unknown kind is never asked the treatment question by accident).
 */
export function normalizeAuthorityKind(input) {
  if (input == null) return null;
  if (typeof input === 'object') {
    const fam = input.family != null ? String(input.family) : null;
    if (fam && REGISTRY[fam]) {
      const sub = input.subtype != null && REGISTRY[fam].includes(String(input.subtype)) ? String(input.subtype) : REGISTRY[fam][0];
      return { family: fam, subtype: sub };
    }
    const raw = input.source_type ?? input.type ?? input.kind ?? input.subtype ?? null;
    const cite = input.full_citation ?? input.citation ?? '';
    return normalizeAuthorityKind(raw == null ? null : `${raw}`) && refineByCitation(normalizeAuthorityKind(`${raw}`), cite);
  }
  const s = String(input).trim();
  if (!s) return null;
  if (REGISTRY[s]) return { family: s, subtype: REGISTRY[s][0] };
  for (const [family, subs] of Object.entries(REGISTRY)) if (subs.includes(s)) return { family, subtype: s };
  if (RECORD_TYPES.has(s.toLowerCase())) return { family: RECORD_KIND, subtype: null };
  const fam = SOURCE_TYPE_FAMILY[s.toUpperCase()];
  if (fam) return { family: fam, subtype: fam === 'secondary' ? 'courtdoc' : REGISTRY[fam][0] };
  return null;
}
function refineByCitation(kind, cite) {
  if (kind && kind.family === 'statute' && CFR_RE.test(String(cite || ''))) return { family: 'regulation', subtype: 'cfr' };
  return kind;
}

/** treatmentRegister(kindOrInput) → 'treatment' | 'currency' | 'none'. Unknown → 'none'. */
export function treatmentRegister(kindOrInput) {
  const k = kindOrInput && typeof kindOrInput === 'object' && kindOrInput.family && REGISTRY[kindOrInput.family] && kindOrInput.subtype
    ? kindOrInput : normalizeAuthorityKind(kindOrInput);
  if (!k || !REGISTRY[k.family]) return REGISTER.NONE;
  return SUBTYPE_REGISTER[k.subtype] || FAMILY_REGISTER[k.family] || REGISTER.NONE;
}

/** 🔴 THE GATE. May a treatment verdict be requested of this authority? Positive allow-list only. */
export function treatmentApplies(kindOrInput) {
  const k = normalizeAuthorityKind(kindOrInput);
  return !!k && TREATABLE_FAMILIES.includes(k.family);
}

/**
 * What a consumer that needs a goodLaw-shaped object should carry for a kind the question does not
 * apply to. `status:'na'` is the tier goodLawTier() already reserves for "good-law does not apply",
 * so every existing switch renders it honestly; `reason` says WHY in the kind's own terms.
 */
export const KIND_NA_REASON = Object.freeze({
  statute: 'Not a treated authority — a statute is amended or repealed, never overruled. Its health is currency.',
  regulation: 'Not a treated authority — a regulation is amended or removed, never overruled. Its health is currency.',
  constitution: 'Not a treated authority — a constitutional provision is amended, never overruled.',
  legislative: 'Not a treated authority — legislative materials are not subject to citator treatment.',
  webpage: 'Not a treated authority — a web page is not subject to citator treatment.',
  secondary: 'Not a treated authority — a secondary source is not subject to citator treatment.',
  [RECORD_KIND]: 'Not an authority — a record document is never subject to citator treatment.',
});
export function notTreatableStatus(kindOrInput) {
  const k = normalizeAuthorityKind(kindOrInput);
  const family = k ? k.family : null;
  return {
    status: 'na',
    register: treatmentRegister(k),
    reason: (family && KIND_NA_REASON[family]) || 'Not a treated authority — the citator question does not apply to this kind of source.',
  };
}

// Treatments a statute (never a case) performs. Both spellings the flags table holds.
const SUPERSESSION = new Set(['superseded_by_statute', 'superseded_statute']);

/**
 * categoryError({ treatment, targetKind, treaterKind }) → null | { code, reason }
 *
 * The structural rule RUN EB's direction-impossible check models, in the type dimension. A verdict
 * that fails it is not "probably wrong" — it names a relationship that cannot exist:
 *   treatment_of_non_case    — a treatment verdict on a kind outside TREATABLE_FAMILIES.
 *   sbs_treater_is_case      — "superseded by statute" naming a CASE as the superseding actor. A case
 *                              may RECOGNISE that a statute superseded a holding; it never performs
 *                              the supersession (GE4 B2's finding; the Frye/Daubert family).
 *   sbs_on_constitution      — "superseded by statute" on a constitutional holding.
 * `treaterKind` is whatever the caller knows: a kind, or 'case' when the row names a cluster id.
 */
export function categoryError({ treatment = null, targetKind = 'case', treaterKind = null } = {}) {
  const t = String(treatment || '').toLowerCase();
  // The constitutional arm is checked FIRST so the row is named for what it is: a constitutional
  // provision is not treatable either, but "superseded by statute on a constitution" is the specific
  // impossibility RUN DC/EB measured, and the census should count it under its own name.
  if (SUPERSESSION.has(t)) {
    const tk = normalizeAuthorityKind(targetKind);
    if (tk && tk.family === 'constitution') return { code: 'sbs_on_constitution', reason: 'a statute cannot supersede a constitutional holding' };
  }
  if (!treatmentApplies(targetKind)) {
    const k = normalizeAuthorityKind(targetKind);
    return { code: 'treatment_of_non_case', reason: `a treatment verdict was requested of a ${k ? k.family : 'non-registry'} authority` };
  }
  if (SUPERSESSION.has(t)) {
    const rk = treaterKind == null ? null : normalizeAuthorityKind(treaterKind);
    if (rk && rk.family === 'case') return { code: 'sbs_treater_is_case', reason: 'a case cannot supersede by statute — the superseding instrument is the statute' };
  }
  return null;
}

export default {
  REGISTRY, REGISTRY_FAMILIES, REGISTRY_SUBTYPES, RECORD_KIND, TREATABLE_FAMILIES, REGISTER, KIND_NA_REASON,
  normalizeAuthorityKind, treatmentRegister, treatmentApplies, notTreatableStatus, categoryError,
};
